import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, siteInfo } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - ${siteInfo.name} | 必赢亚洲官网`;
    } else {
      document.title = `分类未找到 - ${siteInfo.name}`;
    }
  }, [category]);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-2">分类未找到</h1>
          <p className="text-foreground-500">抱歉，您访问的分类不存在。</p>
          <a href="/" className="mt-4 inline-block text-primary-500 font-medium hover:underline">返回首页</a>
        </div>
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${category.name} - ${siteInfo.name}`,
    description: category.description,
    url: `${siteInfo.url}/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: category.name },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <main className="min-h-screen bg-background-50">
        <div className="pt-16 md:pt-18">
          <Breadcrumb items={breadcrumbItems} />

          <section className="w-full px-4 md:px-6 py-10 md:py-16">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10">
                <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 bg-primary-50 rounded-2xl flex items-center justify-center">
                  <i className={`${category.icon} text-3xl md:text-4xl text-primary-500`}></i>
                </div>
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                  {category.name}
                </h1>
                <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                  {category.description}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mb-12">
                {category.features.map((feature, index) => (
                  <div key={index} className="bg-background-100 rounded-xl p-5 md:p-6 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 bg-secondary-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-secondary-600"></i>
                    </div>
                    <p className="text-sm font-medium text-foreground-800">{feature}</p>
                  </div>
                ))}
              </div>

              <div className="bg-background-100 rounded-2xl p-8 md:p-12 text-center">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                  准备好开始了吗？
                </h2>
                <p className="text-sm text-foreground-500 mb-6 max-w-lg mx-auto">
                  立即加入必赢亚洲，体验{category.name}的无限乐趣。新会员注册即享丰厚大礼包。
                </p>
                <a
                  href="/category/promotions"
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
                >
                  立即注册
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}